// src/components/chat/InviteFlowOutgoing.jsx
// Outgoing invite UI: declined view with cooldown + pending/sent view with textarea.
import React from 'react';

export default function InviteFlowOutgoing({
  // Declined view props
  isDeclinedView,
  declineDateLabel,
  declineNoteFromThread,
  headerTitle,
  cooldownLabel,
  isCooldownActive,
  onRetry,
  // Pending/sent view props
  inviteSent,
  retryMode,
  inviteMessage,
  onInviteMessageChange,
  onCancel,
  onSend,
  isSending,
}) {
  if (isDeclinedView) {
    return (
      <>
        <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-6 bg-gray-50">
          <div className="max-w-xl mx-auto space-y-4">
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 text-sm text-gray-900">
              <strong className="block text-base text-gray-900">
                This user has declined your invitation to chat.
              </strong>
              <p className="mt-1 text-gray-700">
                This conversation will remain active. You will be able
                to invite them again after 30 days. They will also be able to accept your
                invitation in the future if they change their mind.
              </p>
              {declineDateLabel && (
                <div className="mt-3 text-[11px] text-gray-500 text-right">
                  {declineDateLabel}
                </div>
              )}
            </div>

            {declineNoteFromThread && (
              <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm">
                <div className="text-xs font-semibold text-gray-500 mb-1">
                  {headerTitle} said:
                </div>
                <p className="text-gray-800 italic whitespace-pre-wrap">
                  {declineNoteFromThread}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="py-4 pl-4 pr-6 border-t border-gray-200 bg-white flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {cooldownLabel}
          </div>
          <button
            type="button"
            className={`px-6 py-1.5 rounded-md text-sm font-medium ${
              isCooldownActive
                ? 'border border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
                : 'border border-blue-500 bg-blue-500 text-white hover:bg-blue-600'
            }`}
            disabled={isCooldownActive}
            onClick={onRetry}
          >
            Try again
          </button>
        </div>
      </>
    );
  }

  // Pending / sent view
  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-6 bg-gray-50">
        {inviteSent && !retryMode ? (
          <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-4 text-sm text-green-800">
            <strong className="block text-base">
              Chat request sent successfully!
            </strong>
            <p className="mt-1 text-gray-700">
              You will be notified once the user accepts or declines
              your invitation.
            </p>
          </div>
        ) : (
          <>
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4 text-sm text-amber-800">
              <strong>Pending Chat Request</strong>
              <p className="mt-1 text-gray-700">
                Write an optional message to include with your chat
                invitation.
              </p>
            </div>

            <textarea
              className="w-full min-h-[120px] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#00aef7] bg-white"
              placeholder="Hi. You look like you might be a good fit for our team. Let's connect!"
              value={inviteMessage}
              onChange={(e) => onInviteMessageChange(e.target.value)}
            />
          </>
        )}
      </div>

      <div className="py-4 pl-4 pr-6 border-t border-gray-200 bg-white flex justify-end gap-2">
        {inviteSent ? (
          <button
            type="button"
            className="px-6 py-1.5 rounded-md bg-gray-300 text-gray-600 text-sm cursor-default"
            disabled
          >
            Invite Sent
          </button>
        ) : (
          <>
            <button
              type="button"
              className="px-6 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-3 py-1.5 rounded-md bg-[#00aef7] text-white hover:bg-[#099ad9] text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={onSend}
              disabled={isSending}
            >
              {isSending ? 'Sending…' : 'Send Invite'}
            </button>
          </>
        )}
      </div>
    </>
  );
}
